import {Entity} from '@s-ui/domain'

import {LocationValueObject} from '../../location/ValueObjects/LocationValueObject.js'
import {PriceValueObject} from '../../price/ValueObjects/PriceValueObject.js'
import {ResourcesValueObject} from '../../resources/ValueObjects/ResourcesValueObject.js'
import {SellerValueObject} from '../../seller/ValueObjects/SellerValueObject.js'
import {BooleanSharedValueObject} from '../../shared/ValueObjects/BooleanSharedValueObject.js'
import {IdSharedValueObject} from '../../shared/ValueObjects/IdSharedValueObject.js'
import {SpecificationsVehiclesValueObject} from '../../vehicles/ValueObjects/SpecificationsVehiclesValueObject.js'
import {WarrantyVehiclesValueObject} from '../../vehicles/ValueObjects/WarrantyVehiclesValueObject.js'

// This Entity is still work in progress, please do not use it yet

export class AdEntity extends Entity {
  #creationDate
  #id
  #isTop = false
  #isUrgent
  #location
  #price
  #publicationDate
  #resources
  #seller
  #title
  #url
  #vehicleSpecifications
  #warranty

  constructor({
    creationDate,
    id,
    isTop,
    isUrgent,
    location,
    price,
    publicationDate,
    resources,
    seller,
    title,
    url,
    vehicleSpecifications,
    warranty
  }) {
    super()
    this.#creationDate = creationDate
    this.#id = id
    this.#isTop = isTop
    this.#isUrgent = isUrgent
    this.#location = location
    this.#price = price
    this.#publicationDate = publicationDate
    this.#resources = resources
    this.#seller = seller
    this.#title = title
    this.#url = url
    this.#vehicleSpecifications = vehicleSpecifications
    this.#warranty = warranty
  }

  static createFromListingApi({ad}) {
    const idValueObject = IdSharedValueObject.createFromPrimitives({id: ad.id})

    const locationValueObject = LocationValueObject.createFromListingApi({location: ad.location})

    const priceValueObject = PriceValueObject.createFromListingApi({isFinanced: ad.isFinanced, price: ad.price})

    const resourcesValueObject = ResourcesValueObject.createFromListingApi({resources: ad.resources, videos: ad.videos})

    const sellerValueObject = SellerValueObject.createFromListingApi({phone: ad.phone, seller: ad.seller})

    const specificationsValueObject = SpecificationsVehiclesValueObject.createFromListingApi({
      bodyTypeId: ad.bodyTypeId,
      cubicCapacity: ad.cubicCapacity,
      drivenWheelsId: ad.drivenWheelsId,
      environmentalLabel: ad.environmentalLabel,
      fuelTypeId: ad.fuelTypeId,
      hasStock: ad.hasStock,
      hp: ad.hp,
      isCertified: ad.isCertified,
      km: ad.km,
      makeId: ad.makeId,
      modelId: ad.modelId,
      offerTypeId: ad.offerType.id,
      transmissionTypeId: ad.transmissionTypeId,
      year: ad.year
    })

    const warrantyValueObject = WarrantyVehiclesValueObject.createFromListingApi({warranty: ad.warranty || {}})

    return new AdEntity({
      creationDate: ad.creationDate,
      description: ad.description,
      id: idValueObject,
      isTop: BooleanSharedValueObject.createWithoutEmptyValue({value: ad.isTop}),
      isUrgent: BooleanSharedValueObject.createWithoutEmptyValue({value: ad.hasUrge}),
      location: locationValueObject,
      price: priceValueObject,
      publicationDate: ad.publishedDate,
      resources: resourcesValueObject,
      seller: sellerValueObject,
      title: ad.title,
      url: ad.url,
      vehicleSpecifications: specificationsValueObject,
      warranty: warrantyValueObject
    })
  }

  get legacyAdContract() {
    return {
      bodyTypeId: this.#vehicleSpecifications.bodyTypeId,
      creationDate: this.#creationDate,
      environmentalLabel: this.#vehicleSpecifications.environmentalLabel,
      financedPrice: this.#price.financedAmount,
      financingInfo: this.#price.financingInfo,
      fuelType: this.#vehicleSpecifications.fuelType,
      fuelTypeId: this.#vehicleSpecifications.fuelTypeId,
      hasStock: this.#vehicleSpecifications.hasStock,
      hasWarranty: this.#vehicleSpecifications.hasWarranty,
      hp: this.#vehicleSpecifications.hp,
      id: this.#id.value,
      img: this.#resources.firstImageUrl,
      includesTaxes: this.#price.hasTaxes,
      isCertified: this.#vehicleSpecifications.isCertified,
      isFinanced: this.#price.isFinanced,
      isProfessional: this.#seller.isProfessional,
      isTop: this.#isTop,
      isUrgent: this.#isUrgent,
      km: this.#vehicleSpecifications.km,
      location: this.#location.value,
      offerType: this.#vehicleSpecifications.offerType,
      pack: this.#seller.pack,
      phone: this.#seller.phone,
      photos: this.#resources.imagesUrls,
      price: this.#price.amount,
      priceAverageIndicator: this.#price.averageIndicator,
      priceRankIndicator: this.#price.rankIndicator,
      provinceId: this.#location.firstProvinceId,
      publicationDate: this.#publicationDate,
      seller: this.#seller.value,
      specificFuelTypeId: this.#vehicleSpecifications.fuelTypeId,
      taxTypeId: this.#price.taxTypeId,
      title: this.#title,
      url: this.#url,
      videos: this.#resources.videos,
      warrantyId: this.#warranty.id,
      warrantyMonths: this.#warranty.months,
      warrantyText: this.#warranty.name,
      year: this.#warranty.year
    }
  }

  get creationDate() {
    return this.#creationDate
  }

  get id() {
    return this.#id.value
  }

  get isTop() {
    return this.#isTop.value
  }

  get isUrgent() {
    return this.#isUrgent.value
  }

  get location() {
    return this.#location.value
  }

  get price() {
    return this.#price.value
  }

  get publicationDate() {
    return this.#publicationDate
  }

  get resources() {
    return this.#resources.value
  }

  get seller() {
    return this.#seller.value
  }

  get title() {
    return this.#title
  }

  get url() {
    return this.#url
  }

  get vehicleSpecifications() {
    return this.#vehicleSpecifications.value
  }

  get warranty() {
    return this.#warranty.value
  }

  get value() {
    return {
      creationDate: this.creationDate,
      id: this.id,
      isTop: this.isTop,
      isUrgent: this.isUrgent,
      location: this.location,
      price: this.price,
      publicationDate: this.publicationDate,
      resources: this.resources,
      seller: this.seller,
      title: this.title,
      url: this.url,
      vehicleSpecifications: this.vehicleSpecifications,
      warranty: this.warranty
    }
  }
}
